import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import {
  Container, PageLayout, Headline, MutedText, Button, Input, Label, Pill, ErrorBanner,
} from "../components/ui";
import { useChildren } from "../lib/childrenStore";
import { useAuth } from "../lib/auth";
import { createStory, InsufficientCreditsError } from "../lib/dataSource";
import { isPlus } from "../lib/plan";
import { UNIVERSES } from "../lib/universes";
import type { Universe } from "../lib/types";

export default function Create() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { me } = useAuth();
  const { currentChild } = useChildren();
  const [universe, setUniverse] = useState<Universe | null>(null);
  const [seed, setSeed] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [quotaBlocked, setQuotaBlocked] = useState(false);

  const suggestions: string[] = universe
    ? (t(`create.seedSuggestions.${universe}`, { returnObjects: true }) as unknown as string[])
    : [];

  async function submit() {
    if (!currentChild || !universe) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await createStory({
        child_id: currentChild.id,
        universe,
        // The doudou is no longer chosen per story — it always comes from
        // the child's profile now (server-side, `ChouetteChild.companion`).
        // The frontend only ever offers the 5-minute format for now; the API
        // still accepts 10/15, reserved for a future "Grande histoire" format.
        duration_min: 5,
        seed: seed || undefined,
      });
      // `universe` rides along so the generation screen has something
      // to show while the story has neither title nor cover yet.
      navigate(`/generation/${res.job_id}?story=${res.story_id}&universe=${universe}`);
    } catch (e) {
      if (e instanceof InsufficientCreditsError) setQuotaBlocked(true);
      else setError(String(e));
    } finally {
      setSubmitting(false);
    }
  }

  if (quotaBlocked) {
    return (
      <PageLayout>
        <Container className="pt-16 flex flex-col items-center text-center gap-4">
          <Headline>{isPlus(me) ? t("quota.title") : t("quota.freeTitle")}</Headline>
          <MutedText>{t("quota.planHint")}</MutedText>
          {/* L'abonnement d'abord, le catalogue en repli : c'est le seul
              endroit du parcours où le parent bute vraiment sur la limite. */}
          <Button onClick={() => navigate("/plus")}>{t("quota.cta")}</Button>
          <Button variant="ghost" onClick={() => navigate("/catalog")}>{t("quota.backToCatalog")}</Button>
        </Container>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <Container className="pt-8 flex flex-col gap-6">
        <Headline>{t("create.title")}</Headline>

        {!currentChild ? (
          <MutedText>{t("home.noChild")}</MutedText>
        ) : (
          <>
            <div>
              <Label>{t("create.universe")}</Label>
              <div className="grid grid-cols-4 gap-2">
                {UNIVERSES.map(({ key, icon: Icon, labelKey }) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setUniverse(key)}
                    className={`tap-target flex flex-col items-center gap-1 rounded-card border p-2 text-center overprint overprint-sm ${
                      universe === key
                        ? "bg-sun border-sun text-mint-ink ink-coral"
                        : "bg-surface border-line text-violet-soft ink-line"
                    }`}
                  >
                    <Icon size={22} />
                    <span className="text-[10px] font-heading font-semibold leading-tight">{t(labelKey)}</span>
                  </button>
                ))}
              </div>
            </div>

            <MutedText>
              {currentChild.companion
                ? t("create.companionLine", { name: currentChild.companion })
                : t("create.companionMissing")}{" "}
              <Link to="/settings" className="underline text-sun">
                {currentChild.companion ? t("create.companionEditLink") : t("create.companionAddLink")}
              </Link>
            </MutedText>

            <div>
              <Label>{t("create.seedLabel")}</Label>
              <MutedText className="mb-2">{t("create.seedHint")}</MutedText>
              {suggestions.length > 0 && (
                <div className="flex gap-2 flex-wrap mb-2">
                  {suggestions.map((s) => (
                    <Pill key={s} active={seed === s} onClick={() => setSeed(s)}>
                      {s}
                    </Pill>
                  ))}
                </div>
              )}
              <Input
                value={seed}
                onChange={(e) => setSeed(e.target.value)}
                placeholder={t("create.seedFreeText")}
              />
            </div>

            {error && <ErrorBanner>{error}</ErrorBanner>}

            <Button onClick={submit} disabled={!universe || submitting} className="w-full text-base py-4">
              {submitting ? t("create.submitting") : t("create.submit")}
            </Button>
          </>
        )}
      </Container>
    </PageLayout>
  );
}
